import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, RefreshCw, Check, X, ChevronDown, ChevronUp, Loader2, ExternalLink, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin Claims Queue — review, approve, or reject provider-listing claims.
 * Each claim shows the fuzzy NPI-match score so admins can eyeball trust.
 * Auto-approved claims (score ≥72%) still show here for auditability.
 */
export default function AdminClaimsQueue() {
  const [state, setState] = useState({ loading: true, error: null, claims: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("pending_review"); // pending_review | approved | rejected | all
  const [expanded, setExpanded] = useState(null); // claim id
  const [acting, setActing] = useState({}); // { [claimId]: "approve" | "reject" }

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/claims`, { params });
      setState({ loading: false, error: null, claims: data.claims || [] });
    } catch (err) {
      console.error("claims load error:", err);
      setState({ loading: false, error: "Couldn't load claims right now.", claims: [] });
    } finally {
      if (isManual) setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (claimId, action) => {
    setActing(prev => ({ ...prev, [claimId]: action }));
    try {
      await axios.post(`${API}/admin/claims/${claimId}/${action}`);
      toast.success(action === "approve" ? "Claim approved — listing is now theirs." : "Claim rejected.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't ${action} the claim.`);
    } finally {
      setActing(prev => ({ ...prev, [claimId]: null }));
    }
  };

  const scoreBadge = (score) => {
    const pct = Math.round((score || 0) * 100);
    let cls = "bg-red-100 text-red-800 border-red-200";
    let label = "Low match";
    if (pct >= 72) { cls = "bg-emerald-100 text-emerald-800 border-emerald-200"; label = "Strong match"; }
    else if (pct >= 50) { cls = "bg-amber-100 text-amber-800 border-amber-200"; label = "Weak match"; }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-body font-semibold ${cls}`}>
        {label} · {pct}%
      </span>
    );
  };

  const statusBadge = (status) => {
    const map = {
      pending_review: "bg-amber-100 text-amber-800 border-amber-200",
      approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
      rejected: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-body font-semibold uppercase tracking-wide ${map[status] || map.pending_review}`}>
        {status.replace("_", " ")}
      </span>
    );
  };

  const claims = state.claims;
  const pendingCount = claims.filter(c => c.status === "pending_review").length;

  return (
    <section
      data-testid="admin-claims-section"
      className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-heading font-semibold text-text-primary">
            Provider Claims
          </h2>
          {pendingCount > 0 && (
            <span
              data-testid="admin-claims-pending-count"
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-body font-bold bg-accent text-white"
            >
              {pendingCount} pending
            </span>
          )}
        </div>
        <Button
          data-testid="admin-claims-refresh"
          onClick={() => load(true)}
          variant="ghost"
          size="sm"
          disabled={refreshing}
          className="text-primary hover:bg-primary/5 font-body"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <p className="text-sm text-text-secondary font-body mb-4">
        NPI-verified claims for CMS-imported listings. Strong matches (≥72%) auto-approve; everything else lands here.
      </p>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "pending_review", label: "Pending" },
          { id: "approved", label: "Approved" },
          { id: "rejected", label: "Rejected" },
          { id: "all", label: "All" },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-claims-filter-${t.id}`}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1 rounded-full text-xs font-body font-semibold border transition-colors ${
              filter === t.id
                ? "bg-primary text-white border-primary"
                : "bg-white text-primary border-primary/40 hover:bg-primary/5"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state.loading ? (
        <div className="py-8 text-center text-text-secondary font-body text-sm">Loading claims…</div>
      ) : state.error ? (
        <div className="py-6 text-center text-red-600 font-body text-sm flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" /> {state.error}
        </div>
      ) : claims.length === 0 ? (
        <div
          data-testid="admin-claims-empty"
          className="py-8 text-center border border-dashed border-[#E2E8F0] rounded-xl bg-[#FAFAFA]"
        >
          <p className="font-body text-text-secondary text-sm">
            {filter === "pending_review" ? "No claims waiting for review." : "No claims in this bucket."}
          </p>
        </div>
      ) : (
        <ul data-testid="admin-claims-list" className="space-y-3">
          {claims.map(c => {
            const isOpen = expanded === c.id;
            const isPending = c.status === "pending_review";
            const busy = acting[c.id];
            return (
              <li
                key={c.id}
                data-testid={`admin-claim-${c.id}`}
                className="border border-[#E2E8F0] rounded-xl bg-white hover:border-primary/40 transition-colors"
              >
                <button
                  type="button"
                  data-testid={`admin-claim-toggle-${c.id}`}
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                  className="w-full text-left px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="flex flex-col min-w-0 flex-1">
                    <p className="font-body font-bold text-text-primary truncate">
                      {c.provider_name}
                    </p>
                    <p className="text-xs font-body text-text-secondary truncate">
                      Claimed by {c.contact_name}{c.role ? ` · ${c.role}` : ""} · {c.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {scoreBadge(c.nppes_match_score)}
                    {statusBadge(c.status)}
                    {isOpen ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-[#E2E8F0] pt-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-body">
                      <div>
                        <p className="text-xs text-text-secondary uppercase tracking-wide">Listing (Tyvena)</p>
                        <p className="font-semibold text-text-primary">{c.provider_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary uppercase tracking-wide">NPPES (CMS Registry)</p>
                        <p className="font-semibold text-text-primary">
                          {c.nppes_org_name || <span className="italic text-text-secondary">NPI not found in NPPES</span>}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary uppercase tracking-wide">NPI</p>
                        <p className="font-mono text-text-primary">
                          {c.npi}{" "}
                          <a
                            href={`https://npiregistry.cms.hhs.gov/provider-view/${c.npi}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-0.5"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-text-secondary uppercase tracking-wide">Phone</p>
                        <p className="text-text-primary">{c.phone}</p>
                      </div>
                    </div>

                    {c.note && (
                      <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
                        <p className="text-xs text-text-secondary uppercase tracking-wide mb-1">Note from claimant</p>
                        <p className="text-sm font-body text-text-primary whitespace-pre-wrap">{c.note}</p>
                      </div>
                    )}

                    <p className="text-xs font-body text-text-secondary">
                      Submitted {new Date(c.created_at).toLocaleString()}
                      {c.reviewed_at && ` · Reviewed ${new Date(c.reviewed_at).toLocaleString()}`}
                      {c.reviewed_by && ` by ${c.reviewed_by}`}
                      {c.auto_approved && " · Auto-approved"}
                    </p>

                    {isPending && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          data-testid={`admin-claim-approve-${c.id}`}
                          onClick={() => act(c.id, "approve")}
                          disabled={!!busy}
                          className="h-10 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-body font-bold"
                        >
                          {busy === "approve" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                          Approve
                        </Button>
                        <Button
                          data-testid={`admin-claim-reject-${c.id}`}
                          onClick={() => act(c.id, "reject")}
                          disabled={!!busy}
                          variant="outline"
                          className="h-10 rounded-full border-2 border-red-500 text-red-600 hover:bg-red-50 font-body font-bold"
                        >
                          {busy === "reject" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
